"use client";

import { useState } from "react";
import { FormDefinition, FormSubmissionResponse } from "../types";
import { HubspotSubmissionField } from "../repository/hubspot.client";
import { getFriendlyCaptchaToken } from "@/lib/friendlycaptcha/client";
import { buildPersonioApplication, PERSONIO_INITIATIVBEWERBUNG_ID } from "../repository/personio.mapper";
import { pickUtmParams, readUtmCookie } from "../utm";
import { uploadPartName } from "../uploads";
interface UseHubspotFormProps {
  formDefinition: FormDefinition;
  /** When true, build a Personio application payload alongside the HubSpot submission. */
  transferPersonio?: boolean;
  /** Personio job position id (defaults to Initiativbewerbung). */
  jobPositionId?: number;
}

/**
 * The visit's campaign params, narrowed to the ones this form declares —
 * HubSpot rejects the whole submission if a field is not in the form
 * definition, and most forms carry no utm_* fields. The current URL wins over
 * the cookie stash (see `../utm`).
 */
const readUtmParams = (declared: Map<string, string>): Record<string, string> => {
  if (typeof window === "undefined") return {};
  const utm = {
    ...readUtmCookie(document.cookie),
    ...pickUtmParams(new URLSearchParams(window.location.search))
  };
  return Object.fromEntries(Object.entries(utm).filter(([name]) => declared.has(name)));
};
export const buildSubmissionFields = (formDefinition: FormDefinition, values: Record<string, unknown>): HubspotSubmissionField[] => {
  const objectTypeIdByName = new Map<string, string>();
  for (const field of formDefinition.fields) {
    objectTypeIdByName.set(field.name, field.objectTypeId);
  }
  for (const hidden of formDefinition.hiddenFields) {
    objectTypeIdByName.set(hidden.name, hidden.objectTypeId);
  }
  const merged: Record<string, unknown> = {
    ...readUtmParams(objectTypeIdByName),
    ...values
  };
  const submission: HubspotSubmissionField[] = [];
  for (const [name, value] of Object.entries(merged)) {
    if (value === undefined || value === null || value === "") continue;
    // Files travel as multipart parts and are added server-side once uploaded;
    // String(File) would submit the literal "[object File]".
    if (typeof File !== "undefined" && value instanceof File) continue;
    // Consent checkboxes are submitted via legalConsentOptions, not as fields.
    if (name.startsWith("consent-")) continue;
    const objectTypeId = objectTypeIdByName.get(name) ?? "0-1";
    submission.push({
      name,
      value,
      objectTypeId
    });
  }
  return submission;
};
export function useHubspotForm({
  formDefinition,
  transferPersonio,
  jobPositionId
}: UseHubspotFormProps) {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submitForm = async (data: Record<string, unknown>, captchaToken?: string): Promise<FormSubmissionResponse> => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      // Visible-widget token when available; headless fallback for the WebMCP
      // agent path (no widget) or a submit fired before the widget solved.
      const token = captchaToken ?? (await getFriendlyCaptchaToken());
      const payload = JSON.stringify({
        formId: formDefinition.id,
        data: buildSubmissionFields(formDefinition, data),
        // Server rebuilds legalConsentOptions from these booleans + the
        // form definition — the consent record itself is not client-authored.
        consents: Object.fromEntries(Object.entries(data).filter(([name]) => name.startsWith("consent-"))),
        captchaToken: token,
        personio: transferPersonio ? buildPersonioApplication(formDefinition, data, jobPositionId ?? PERSONIO_INITIATIVBEWERBUNG_ID) : undefined,
        context: {
          pageUri: typeof window !== "undefined" ? window.location.href : "",
          pageName: typeof document !== "undefined" ? document.title : ""
        }
      });

      // Only forms with a picked file switch to multipart; every other form
      // sends exactly the JSON request it always did.
      const files = Object.entries(data).filter((entry): entry is [string, File] => typeof File !== "undefined" && entry[1] instanceof File);
      const response = await fetch("/api/hubspot/submit", {
        method: "POST",
        ...(files.length ? {
          body: (() => {
            const form = new FormData();
            form.append("payload", payload);
            for (const [name_0, file] of files) form.append(uploadPartName(name_0), file, file.name);
            return form;
          })()
        } : {
          headers: {
            "Content-Type": "application/json"
          },
          body: payload
        })
      });
      const result: FormSubmissionResponse = await response.json();
      if (result.success) {
        setSuccess(true);
        return {
          success: true
        };
      }
      const message_0 = result.message || "Submission failed";
      setError(message_0);
      return {
        success: false,
        message: message_0
      };
    } catch (err) {
      const message = err instanceof Error ? err.message : "An unexpected error occurred";
      setError(message);
      return {
        success: false,
        message
      };
    } finally {
      setLoading(false);
    }
  };
  return {
    submitForm,
    loading,
    success,
    error
  };
}